import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useDateLocale } from '../../utils/dateLocale';
import { CalendarDays, Check, Icon } from '../ui/Icon';
import { itineraryApi } from '../../api/itinerary.api';
import { venueActivityPayload, activitiesForVenue } from '../../utils/venueScheduling';
import type { Venue, ItineraryDay, Activity } from '../../types';
import Spinner from '../ui/Spinner';
import { toast } from '../ui/Toast';

export interface AddToItinerarySaveResult {
  added: Array<{ activity: Activity; dayId: string }>;
  removed: Array<{ activityId: string; dayId: string }>;
}

interface Props {
  venue: Venue;
  days: ItineraryDay[];
  tripId: string;
  onSaved: (result: AddToItinerarySaveResult) => void;
  onCancel: () => void;
}

export default function AddToItineraryModal({ venue, days, tripId, onSaved, onCancel }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  // dayId → activities already linking this venue to that day.
  const dayLinks = useMemo(() => {
    const map = new Map<string, Activity[]>();
    for (const day of days) {
      const linked = activitiesForVenue(day.activities, venue);
      if (linked.length > 0) map.set(day.id, linked);
    }
    return map;
  }, [days, venue]);

  const initialDayIds = useMemo(() => new Set(dayLinks.keys()), [dayLinks]);

  const [selectedDayIds, setSelectedDayIds] = useState<Set<string>>(() => new Set(initialDayIds));
  const [loading, setLoading] = useState(false);

  const isEditing = initialDayIds.size > 0;

  const { toAdd, toRemove } = useMemo(() => {
    const add = [...selectedDayIds].filter(id => !initialDayIds.has(id));
    const remove = [...initialDayIds].filter(id => !selectedDayIds.has(id));
    return { toAdd: add, toRemove: remove };
  }, [selectedDayIds, initialDayIds]);

  const hasChanges = toAdd.length > 0 || toRemove.length > 0;

  function toggleDay(id: string) {
    setSelectedDayIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSave() {
    if (!hasChanges) return;
    setLoading(true);
    try {
      const [addedResults, removedResults] = await Promise.all([
        Promise.all(
          toAdd.map(dayId =>
            itineraryApi.createActivity(tripId, dayId, venueActivityPayload(venue))
              .then(({ activity }) => ({ activity, dayId })),
          ),
        ),
        Promise.all(
          toRemove.flatMap(dayId =>
            (dayLinks.get(dayId) ?? []).map(act =>
              itineraryApi.deleteActivity(tripId, dayId, act.id).then(() => ({ activityId: act.id, dayId })),
            ),
          ),
        ),
      ]);

      if (addedResults.length > 0 && removedResults.length === 0) {
        if (addedResults.length === 1) {
          const n = days.find(d => d.id === addedResults[0].dayId)?.dayNumber;
          toast(t('itinerary.modal.addedToDay', { n }), 'success');
        } else {
          toast(t('itinerary.modal.addedToDays', { count: addedResults.length }), 'success');
        }
      } else if (removedResults.length > 0 && addedResults.length === 0) {
        toast(t('itinerary.modal.removedFromItinerary'), 'success');
      } else {
        toast(t('itinerary.modal.itinerarySaved'), 'success');
      }

      onSaved({ added: addedResults, removed: removedResults });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error');
      toast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }

  if (days.length === 0) {
    return (
      <div className="text-center py-6">
        <Icon as={CalendarDays} size={24} className="text-slate-200 mx-auto mb-2" />
        <p className="text-sm text-slate-500">{t('itinerary.modal.noDaysYet')}</p>
        <p className="text-xs text-slate-400 mt-1">{t('itinerary.modal.noDaysDesc')}</p>
        <button onClick={onCancel} className="btn-secondary mt-4 text-xs">{t('common.close')}</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Venue summary */}
      <div className="px-3 py-2.5 bg-slate-50 rounded-xl border border-slate-100">
        <p className="text-xs font-semibold text-slate-700 truncate">{venue.name}</p>
        {venue.zone && <p className="text-[11px] text-slate-400 mt-0.5">{venue.zone}</p>}
      </div>

      {/* Day selector — multi-select. Deselecting a currently-scheduled day removes it. */}
      <div>
        <p className="text-xs font-semibold text-slate-600 mb-2">{t('itinerary.modal.dayLabel')}</p>
        <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
          {days.map(day => {
            const selected = selectedDayIds.has(day.id);
            return (
              <button
                key={day.id}
                onClick={() => toggleDay(day.id)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-xl border text-left transition-all cursor-pointer text-xs ${
                  selected
                    ? 'border-teal-400 bg-teal-50 text-teal-800 font-semibold'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                }`}
              >
                <span>{t('itinerary.dayLabel', { n: day.dayNumber })}{day.title ? ` — ${day.title}` : ''}</span>
                <span className="flex items-center gap-2">
                  <span className="text-slate-400 font-normal text-[11px]">
                    {format(parseISO(day.date), 'EEE, MMM d', { locale: dateLocale })}
                  </span>
                  {selected && <Icon as={Check} size={14} className="text-teal-600 shrink-0" />}
                </span>
              </button>
            );
          })}
        </div>
        {isEditing && (
          <p className="text-[11px] text-slate-400 mt-2">{t('itinerary.modal.unassignHint')}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <button
          onClick={handleSave}
          disabled={loading || !hasChanges}
          className="btn-primary flex-1 gap-1.5 justify-center"
        >
          {loading ? <Spinner size="sm" /> : <Icon as={CalendarDays} size={14} />}
          {isEditing
            ? t('common.save')
            : selectedDayIds.size > 1
              ? t('itinerary.modal.addToDays', { count: selectedDayIds.size })
              : t('itinerary.modal.addToItinerary')}
        </button>
        <button onClick={onCancel} className="btn-secondary">{t('common.cancel')}</button>
      </div>
    </div>
  );
}
